import * as Sentry from '@sentry/react';
import type { Dispatch } from '@reduxjs/toolkit';

import { isDeploymentUnreachableError, serverMessageOf, statusOf } from './thunk';
import type {
  OfflineModeBroadcastDirection,
  OfflineModeBroadcastPrepare,
  OfflineModeBroadcastPrepareErrorCode,
  OfflineModeBroadcastRefusal,
  OfflineModeBroadcastRefusalCode,
  OfflineModeBroadcastSendInput,
  OfflineModeBroadcastSendResult,
  OfflineModeBroadcastServerTemplate,
} from './index';
import TCClient from 'client';
import { SET_TOAST_DURATION_IN_MILLISECONDS } from 'admin/utils/setToastDuration';
import { actions, store, thunk } from 'redux-stores';
import { localServerRequestOptions } from 'common/utils/offlineMode/localServerAuthHeaders';

const REFUSAL_CODES = [
  'empty_body',
  'body_too_large',
  'invalid_template',
  'no_recipients',
  'phi_suspected',
  'no_recorded_admin_action',
  'forbidden',
  'broadcast_rate_limited',
  'roster_unavailable',
  'not_local_node',
  'fanout_failed',
  'unreachable',
  'send_failed',
] as const;

const formatCount = (value: number): string => value.toLocaleString('en-US');

const isRecord = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === 'object' && !Array.isArray(value);

const count = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) && value >= 0 ? Math.floor(value) : null;

const text = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed || undefined;
};

const timestamp = (value: unknown): number | undefined =>
  typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : undefined;

const parseDirection = (value: unknown): OfflineModeBroadcastDirection | null =>
  value === 'to_offline' || value === 'to_cloud' ? value : null;

const parseServerTemplate = (value: unknown): OfflineModeBroadcastServerTemplate | null =>
  value === 'activation' || value === 'return' ? value : null;

const parseTemplateBody = (value: unknown): string | null => {
  if (!isRecord(value)) return null;
  return text(value.body) ?? null;
};

export function parseBroadcastPrepare(raw: unknown): OfflineModeBroadcastPrepare | null {
  if (!isRecord(raw)) return null;

  const direction = parseDirection(raw.direction);
  const defaultTemplate = parseServerTemplate(raw.default_template);
  if (!direction || !defaultTemplate) return null;

  const templatesWire = isRecord(raw.templates) ? raw.templates : null;
  const activation = parseTemplateBody(templatesWire?.activation);
  const activationReturn = parseTemplateBody(templatesWire?.return);
  if (!activation || !activationReturn) return null;

  const scopeWire = isRecord(raw.recipient_scope) ? raw.recipient_scope : null;
  const kind = text(scopeWire?.kind);
  const orgToken = text(scopeWire?.org_token);
  const resolvedCount = count(scopeWire?.resolved_count);
  if (!kind || !orgToken || resolvedCount === null) return null;

  const maxBodyBytes = count(isRecord(raw.limits) ? raw.limits.max_body_bytes : undefined);
  if (maxBodyBytes === null) return null;

  const reachableWire = isRecord(raw.reachable_now) ? raw.reachable_now : null;
  const reachableCount = count(reachableWire?.count);
  const measuredAt = timestamp(reachableWire?.measured_at);

  return {
    direction,
    defaultTemplate,
    templates: { activation, return: activationReturn },
    recipientScope: { kind, orgToken, resolvedCount },
    reachableNow:
      reachableCount !== null && measuredAt ? { count: reachableCount, measuredAt } : null,
    limits: { maxBodyBytes },
  };
}

export function parseBroadcastSendResult(raw: unknown): OfflineModeBroadcastSendResult | null {
  if (!isRecord(raw)) return null;

  const broadcastId = text(raw.broadcast_id);
  const deliveredNow = count(raw.delivered_now);
  const resolvedCount = count(
    isRecord(raw.recipient_scope) ? raw.recipient_scope.resolved_count : undefined
  );
  if (!broadcastId || deliveredNow === null || resolvedCount === null) return null;

  return {
    broadcastId,
    deliveredNow,
    resolvedCount,
  };
}

export function prefixOf(message: string | undefined): OfflineModeBroadcastRefusalCode | null {
  if (!message) return null;
  const prefix = message.split(':', 1)[0].trim();
  return REFUSAL_CODES.find((code) => code === prefix) ?? null;
}

export function parseBroadcastRefusal(err: unknown): OfflineModeBroadcastRefusal {
  if (isDeploymentUnreachableError(err)) return { code: 'unreachable' };

  const message = serverMessageOf(err);
  const code = prefixOf(message);

  if (!code) return statusOf(err) === 403 ? { code: 'forbidden' } : { code: 'send_failed' };
  if (code !== 'phi_suspected') return { code };

  const detail = text((message ?? '').slice(`${code}:`.length));
  return detail ? { code, detail } : { code };
}

export function broadcastPrepareErrorCode(err: unknown): OfflineModeBroadcastPrepareErrorCode {
  if (isDeploymentUnreachableError(err)) return 'unreachable';
  return statusOf(err) === 403 ? 'forbidden' : 'read-failed';
}

export const broadcastPath = (organizationId: string) =>
  `/v2/organization/${organizationId}/offline-mode/broadcast`;

export const fetchOfflineModeBroadcastPrepare = async (
  dispatch: Dispatch,
  organizationId: string
) => {
  if (!organizationId) return;

  dispatch(actions.setOfflineModeBroadcastRefusal(null, organizationId));
  dispatch(actions.setOfflineModeBroadcastPrepareError(null, organizationId));
  dispatch(actions.setOfflineModeBroadcastPrepareStatus('loading', organizationId));

  try {
    const res = await TCClient.httpClient.get(
      broadcastPath(organizationId),
      localServerRequestOptions(TCClient.httpClient)
    );
    const prepare = parseBroadcastPrepare(res.data);

    if (!prepare) {
      Sentry.logger.error(
        Sentry.logger.fmt`[OfflineMode] Unusable broadcast prepare reply for ${organizationId}`,
        { organizationId }
      );
      dispatch(actions.setOfflineModeBroadcastPrepare(null, organizationId));
      dispatch(actions.setOfflineModeBroadcastPrepareError('unusable', organizationId));
      dispatch(actions.setOfflineModeBroadcastPrepareStatus('failed', organizationId));
      return;
    }

    dispatch(actions.setOfflineModeBroadcastPrepare(prepare, organizationId));
    dispatch(actions.setOfflineModeBroadcastPrepareStatus('ready', organizationId));
  } catch (err: unknown) {
    Sentry.logger.error(Sentry.logger.fmt`[OfflineMode] Failed to prepare a broadcast`, { err });

    const code = broadcastPrepareErrorCode(err);

    dispatch(actions.setOfflineModeBroadcastPrepare(null, organizationId));
    dispatch(actions.setOfflineModeBroadcastPrepareError(code, organizationId));
    dispatch(actions.setOfflineModeBroadcastPrepareStatus('failed', organizationId));

    if (code === 'unreachable') {
      dispatch(actions.setOfflineModeDeploymentUnreachable(true, organizationId));
    }

    if (prefixOf(serverMessageOf(err)) === 'roster_unavailable') {
      dispatch(
        actions.setOfflineModeBroadcastRefusal({ code: 'roster_unavailable' }, organizationId)
      );
    }
  }
};

export const sendOfflineModeBroadcast = async (
  dispatch: Dispatch,
  organizationId: string,
  { body, template }: OfflineModeBroadcastSendInput
): Promise<OfflineModeBroadcastSendResult | null> => {
  if (!organizationId) return null;
  if (store.getState().offlineMode.broadcastSending) return null;

  dispatch(actions.setOfflineModeBroadcastRefusal(null, organizationId));
  dispatch(actions.setOfflineModeBroadcastSending(true));

  try {
    const res = await TCClient.httpClient.post(broadcastPath(organizationId), {
      data: { body, template },
      retry: false,
      ...localServerRequestOptions(TCClient.httpClient),
    });

    const result = parseBroadcastSendResult(res.data);

    if (!result) {
      Sentry.logger.error(
        Sentry.logger.fmt`[OfflineMode] Unusable broadcast send reply for ${organizationId}`,
        { organizationId }
      );
      dispatch(actions.setOfflineModeBroadcastRefusal({ code: 'send_failed' }, organizationId));
      return null;
    }

    dispatch(actions.setOfflineModeBroadcastResult(result, organizationId));
    await thunk.fetchOfflineModeEventLog(dispatch, { organizationId });
    thunk.setToastSettings({
      duration: SET_TOAST_DURATION_IN_MILLISECONDS(3),
      open: true,
      message:
        `Sent to ${formatCount(result.resolvedCount)} people. ` +
        `${formatCount(result.deliveredNow)} devices received it now.`,
      type: 'SUCCESS',
    });

    return result;
  } catch (err: unknown) {
    Sentry.logger.error(Sentry.logger.fmt`[OfflineMode] Failed to send a broadcast`, { err });
    const refusal = parseBroadcastRefusal(err);
    dispatch(actions.setOfflineModeBroadcastRefusal(refusal, organizationId));

    if (refusal.code === 'unreachable') {
      dispatch(actions.setOfflineModeDeploymentUnreachable(true, organizationId));
    }

    return null;
  } finally {
    dispatch(actions.setOfflineModeBroadcastSending(false));
  }
};
